/**
 * todoPageContext — which record the person is looking at, read off the URL,
 * so the top bar's "+ To-do" starts already filled in (the to-dos upgrade,
 * 2026-10-04; Michael: "depending on where this todo is coming from, it'll
 * auto populate the information — like client, project, contact, team").
 *
 * THE GATE gives every shareable record a canonical URL, which is what makes
 * this a pure function of the path: the deal open over the board at
 * /sales/cards/42, the project at /operations/projects/7, the ticket at
 * /operations/tickets/9, a client file, a contact, an L10, an issue, a rock.
 * The server turns {kind, id} into client · record · contact · team
 * (GET /api/todos/context); this only finds the {kind, id}.
 */
/** Tells an open to-dos page a to-do was made elsewhere (the top bar), so it
 *  lands in its list without a reload. */
export const TODO_CREATED_EVENT = 'tpl:todo-created';

export type TodoPageKind = 'deal' | 'project' | 'ticket' | 'client' | 'contact' | 'eos_l10' | 'eos_issue' | 'eos_rock';

export interface TodoPageRef { kind: TodoPageKind; id: string }

const RULES: Array<{ re: RegExp; kind: TodoPageKind }> = [
  // Deals open over the board; the pre-c4 shapes still resolve to the deal.
  { re: /^\/sales\/cards\/(\d+)(?:\/|$)/, kind: 'deal' },
  { re: /^\/sales\/pipelines\/[^/]+\/cards\/(\d+)(?:\/|$)/, kind: 'deal' },
  { re: /^\/sales\/deals\/(\d+)(?:\/|$)/, kind: 'deal' },
  { re: /^\/operations\/projects\/(\d+)(?:\/|$)/, kind: 'project' },
  { re: /^\/operations\/tickets\/(\d+)(?:\/|$)/, kind: 'ticket' },
  { re: /^\/sales\/campaigns\/[^/]+\/contacts\/(\d+)(?:\/|$)/, kind: 'contact' },
  { re: /^\/contacts\/(\d+)(?:\/|$)/, kind: 'contact' },
  // A client id is a card id: legacy Aptly ids and native_<ts>_<rand> both.
  { re: /^\/clients\/([^/?#.]+)(?:\/|$)/, kind: 'client' },
  { re: /^\/eos\/meetings\/([^/?#]+)(?:\/|$)/, kind: 'eos_l10' },
  { re: /^\/eos\/ids\/(\d+)(?:\/|$)/, kind: 'eos_issue' },
  { re: /^\/eos\/rocks\/(\d+)(?:\/|$)/, kind: 'eos_rock' },
];

/** The record this page shows, or null (a list, a dashboard, settings…). */
export function pageRecordOf(pathname: string): TodoPageRef | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  for (const r of RULES) {
    const m = r.re.exec(path);
    if (m && m[1]) return { kind: r.kind, id: decodeURIComponent(m[1]) };
  }
  return null;
}

export default pageRecordOf;
